import { Link } from 'react-router-dom'
import {
  Heart, Home as HomeIcon, Leaf, CalendarDays, ShieldCheck,
  SlidersHorizontal, MonitorSmartphone, ClipboardList, Flag,
} from 'lucide-react'
import { Section, SectionHeading, IconCircle, NumberCircle, CheckItem, FAQ, CTABand, SplitHero } from '../components/ui'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'

const whyTiles = [
  { icon: Heart, title: 'Personalized care plans', body: "Every plan is written around your loved one's routines, history and preferences — not a fixed package." },
  { icon: ShieldCheck, title: 'Screened caregivers', body: 'Every caregiver is interviewed, reference-checked and matched for personality as well as skill.' },
  { icon: SlidersHorizontal, title: 'Flexible support', body: 'Hours and services adjust as needs change, with no need to start the process over.' },
  { icon: MonitorSmartphone, title: 'Family portal updates', body: 'Daily notes and schedules appear in the Family Room after every visit.' },
  { icon: ClipboardList, title: 'Clear shift notes & billing', body: 'Itemized hours and receipts mean no surprise fees and no guessing what a visit covered.' },
  { icon: Flag, title: 'Proudly Canadian owned', body: '🍁 A locally owned Calgary company, accountable to the families and caregivers we work with.' },
]

const services = [
  { icon: Heart, title: 'Personal Care Services', blurb: "Respectful help with bathing, dressing, grooming, mobility and daily reminders — always non-medical and always at your loved one's pace.", href: '/services/personal-care' },
  { icon: HomeIcon, title: 'Meal Preparation & Light Housekeeping', blurb: 'Home-cooked meals your loved one actually enjoys, plus tidy laundry, dishes and living spaces that feel calm and cared for.', href: '/services/meals-housekeeping' },
  { icon: Leaf, title: 'Companionship & Outings', blurb: 'Real conversation, shared hobbies, walks in the neighbourhood and visits out — the everyday connection that lifts a whole week.', href: '/services/companionship' },
  { icon: CalendarDays, title: 'Post-Hospital Care', blurb: 'Extra non-medical hands during the first weeks home — meals, rest, routines and reminders, while medical care stays with clinicians.', href: '/services/post-hospital-care' },
  { icon: ShieldCheck, title: 'Specialized Care', blurb: "Patient, familiar support for dementia, Alzheimer's, palliative, end-of-life and respite needs, alongside your healthcare team.", href: '/specialized-care' },
]

const pillars = [
  { n: '1', title: 'Goals, History & Lifestyle', body: 'We start with the person: their story, routines, faith, food, music, pets and what independence means to them.' },
  { n: '2', title: 'Family-Centered Customization', body: 'Families tell us what matters most, and the plan is written around those priorities — not a fixed package.' },
  { n: '3', title: 'Caregiver Matching', body: 'We match on temperament, language, experience and schedule so visits feel like a familiar friend arriving.' },
  { n: '4', title: 'Budget & Pricing Alignment', body: 'We shape hours and services around a budget you set, and we tell you plainly what each option costs.' },
  { n: '5', title: 'Ongoing Communication', body: 'Shift notes, plan reviews and a direct line to your coordinator keep everyone in the loop as needs change.' },
]

const steps = [
  { n: '1', title: 'You send a care request', body: 'Call us or fill out a short form. Tell us who needs support and what a hard day looks like right now — nothing formal required.' },
  { n: '2', title: 'We confirm your service area', body: "We serve Calgary and surrounding communities. We'll confirm we can reach your neighbourhood before anything else." },
  { n: '3', title: 'You choose assessment times', body: 'Pick up to three times that suit your family. In-home, at our Brisebois Drive office, or by phone — whatever feels easiest.' },
  { n: '4', title: 'You meet a care coordinator', body: 'A real person listens first: routines, history, worries, preferences. No pressure and no medical jargon.' },
  { n: '5', title: 'We build your 5-Pillar Care Plan', body: 'Goals and lifestyle, family customization, caregiver matching, budget alignment and ongoing communication, written down clearly.' },
]

const familyRoomItems = [
  'Daily care notes after every visit',
  'Task checklists completed by your caregiver',
  'Upcoming schedule and visit times',
  'A place to leave family notes',
  'Itemized billing receipts',
]

const visitTasks = ['Breakfast & medication reminder', 'Light housekeeping', 'Afternoon walk']

const cdhciPoints = [
  'Choose your own approved home care provider',
  'Care delivered to your approved care plan and hours',
  'Authorized services billed directly to Alberta Blue Cross',
  'Eligibility is determined by your AHS case manager',
]

const resourceTeasers = [
  { title: 'Getting Started Guides', body: 'What to expect from your first call through your first week of care.' },
  { title: 'Caregiving at Home', body: "Practical articles on routines, safety and supporting a loved one's independence." },
  { title: 'Understanding Costs & Funding', body: 'Plain explanations of pricing, budgets and funding options available in Alberta.' },
]

const faqs = [
  { q: 'What does non-medical care actually mean?', a: 'Our caregivers help with daily living — personal care, meals, housekeeping and companionship. They do not provide nursing, diagnosis or medication administration; those stay with your healthcare professionals.' },
  { q: "Can care change as my loved one's needs change?", a: 'Yes. Plans are reviewed regularly, and hours or services can increase, decrease or shift focus at any time without starting the process over.' },
  { q: "How does my family see what's happening at each visit?", a: "The Family Room Portal shows daily care notes, task checklists, schedules and billing receipts after every visit, so you're never left wondering." },
]

export default function Home() {
  return (
    <>
      <SplitHero
        eyebrow="In-home senior care in Calgary"
        title="In-home care that helps your loved one feel safe, supported, and understood."
        lede="True North provides personalized, non-medical home care built around your loved one's routines and preferences — not a one-size-fits-all schedule. From companionship to post-hospital support, our caregivers help your family feel confident every day."
        image="/assets/hero-home.jpg"
        imageAlt="Caregiver sharing a warm moment with a senior client at home"
        actions={
          <>
            <Link to="/schedule-assessment" className="btn btn-primary">Schedule an In-Home Assessment</Link>
            <a href={PHONE_TEL} className="btn btn-secondary">Call {PHONE_DISPLAY}</a>
          </>
        }
      />

      {/* Two-way entry */}
      <Section tone="cream">
        <div className="grid grid-2">
          <div className="card-teal">
            <IconCircle icon={Heart} variant="on-teal" />
            <h3 style={{ margin: '20px 0 0 0', fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-cream)' }}>Seeking Care</h3>
            <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'oklch(0.9765 0.0098 87.47 / 85%)' }}>
              Tell us what's happening at home and we'll walk you through scheduling an in-home assessment — no pressure, no obligation.
            </p>
            <Link to="/schedule-assessment" className="link-inline link-inline-on-dark" style={{ marginTop: 24 }}>
              Schedule an In-Home Assessment
            </Link>
          </div>
          <div className="card-cream">
            <IconCircle icon={ShieldCheck} />
            <h3 style={{ margin: '20px 0 0 0', fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>Seeking a Career</h3>
            <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>
              Join a team of screened, respected caregivers making a real difference in Calgary homes every day.
            </p>
            <Link to="/careers" className="link-inline" style={{ marginTop: 24 }}>See caregiver openings</Link>
          </div>
        </div>
      </Section>

      {/* CDHCI — approved Alberta provider */}
      <Section tone="panel-deep">
        <div className="grid grid-2" style={{ gap: 48, alignItems: 'center' }}>
          <div style={{ minWidth: 0 }}>
            <SectionHeading
              eyebrow="Approved Alberta provider"
              title="Funded home care through CDHCI"
              lede="True North Care & Support is an approved provider for Alberta's Client Directed Home Care Invoicing program. If Alberta Health Services has approved you for home care, you can choose us as your provider — and we invoice Alberta Blue Cross directly for authorized services."
            />
            <div style={{ marginTop: 32 }}>
              <Link to="/client-directed-home-care-invoicing" className="btn btn-primary btn-auto">
                Learn about CDHCI
              </Link>
            </div>
          </div>
          <div className="card-cream" style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {cdhciPoints.map((p) => <CheckItem key={p}>{p}</CheckItem>)}
            </div>
          </div>
        </div>
      </Section>

      {/* Why families choose us */}
      <Section tone="panel">
        <SectionHeading
          eyebrow="Why Families Choose True North"
          title="Care that begins with listening."
          lede="Before we talk about schedules or services, we ask what a good day looks like for your loved one. Everything else is built from there."
        />
        <div className="grid grid-3" style={{ marginTop: 48 }}>
          {whyTiles.map((t) => (
            <div key={t.title} style={{ display: 'flex', gap: 16 }}>
              <IconCircle icon={t.icon} />
              <div>
                <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>{t.title}</h3>
                <p style={{ margin: '8px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Services overview */}
      <Section tone="cream">
        <SectionHeading
          eyebrow="Our Services"
          title="Support that adapts to your family's needs"
          lede="Every service below is non-medical, delivered by screened caregivers, and can be combined into a single care plan."
        />
        <div className="grid grid-3" style={{ marginTop: 48 }}>
          {services.map((s) => (
            <div key={s.title} className="card-teal">
              <IconCircle icon={s.icon} variant="on-teal" />
              <h3 style={{ margin: '20px 0 0 0', fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-cream)' }}>{s.title}</h3>
              <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'oklch(0.9765 0.0098 87.47 / 85%)', flex: 1 }}>{s.blurb}</p>
              <Link to={s.href} className="link-inline link-inline-on-dark" style={{ marginTop: 24 }}>See this service</Link>
            </div>
          ))}
        </div>
      </Section>

      {/* 5-Pillar Care Plan */}
      <Section tone="dark">
        <SectionHeading
          eyebrow="Our Approach"
          title="The 5-Pillar Care Plan"
          lede="Every family receives a written plan built on five pillars, reviewed and adjusted as needs change."
          onDark
        />
        <div className="grid grid-5" style={{ marginTop: 48, gap: 24 }}>
          {pillars.map((p) => (
            <div key={p.n} style={{ display: 'flex', height: '100%', boxSizing: 'border-box', flexDirection: 'column', borderRadius: 8, background: 'var(--color-teal-800)', padding: 24, color: 'var(--color-cream)' }}>
              <NumberCircle n={p.n} onDark size={40} />
              <h3 style={{ margin: '16px 0 0 0', fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-cream)' }}>{p.title}</h3>
              <p style={{ margin: '8px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'oklch(0.9765 0.0098 87.47 / 85%)' }}>{p.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* How it works preview */}
      <Section tone="cream">
        <SectionHeading
          eyebrow="Getting Started"
          title="How It Works"
          lede="From your first call to ongoing family updates, here's what the process looks like."
        />
        <div className="grid grid-2" style={{ marginTop: 48, gap: 40 }}>
          {steps.map((s) => (
            <div key={s.n} style={{ display: 'flex', gap: 20 }}>
              <span style={{ marginTop: 4 }}><NumberCircle n={s.n} /></span>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>{s.title}</h3>
                <p style={{ margin: '8px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>{s.body}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 40 }}>
          <Link to="/how-it-works" className="link-inline">See the full process</Link>
        </div>
      </Section>

      {/* Family Room preview */}
      <Section tone="panel">
        <SectionHeading
          eyebrow="Family Room Portal"
          title="Stay close to the care, wherever you are"
          lede="After every visit, your family can log in and see exactly what happened — no phone tag required."
        />
        <div className="grid grid-2" style={{ marginTop: 48, gap: 40, alignItems: 'start' }}>
          <div className="grid grid-2" style={{ gap: 16 }}>
            {familyRoomItems.map((i) => <CheckItem key={i}>{i}</CheckItem>)}
          </div>
          <div style={{ borderRadius: 8, border: '1px solid var(--color-line)', background: '#FFFFFF', padding: 24, boxShadow: 'var(--shadow-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--color-line)', paddingBottom: 16 }}>
              <div>
                <p style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--color-teal-900)' }}>Amara O.</p>
                <p style={{ margin: 0, fontSize: 15, color: 'var(--color-muted)' }}>Tue · 9:00 AM – 1:00 PM</p>
              </div>
              <span style={{ borderRadius: 4, background: 'oklch(0.6058 0.0491 154.68 / 20%)', padding: '4px 12px', fontSize: 13, fontWeight: 600, color: 'var(--color-sage-deep)' }}>Completed</span>
            </div>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {visitTasks.map((t) => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 17 }}>
                  <span style={{ display: 'flex', height: 24, width: 24, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: 'oklch(0.6058 0.0491 154.68 / 20%)', color: 'var(--color-sage-deep)', fontSize: 13 }}>✓</span>
                  {t}
                </div>
              ))}
            </div>
            <p style={{ margin: '16px 0 0 0', fontSize: 15, lineHeight: 1.6, color: 'var(--color-muted)' }}>
              "Had a great morning — enjoyed oatmeal and a slow walk around the block. In good spirits."
            </p>
          </div>
        </div>
        <div style={{ marginTop: 40, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          <Link to="/family-room" className="btn btn-primary">Explore the Family Room</Link>
          <Link to="/login" className="btn btn-secondary">Log in to the Family Room</Link>
        </div>
      </Section>

      {/* Resources preview */}
      <Section tone="cream">
        <SectionHeading
          eyebrow="Resources"
          title="Helpful reading for every stage"
          lede="Guides and articles written for families navigating in-home care decisions."
        />
        <div className="grid grid-3" style={{ marginTop: 48 }}>
          {resourceTeasers.map((r) => (
            <div key={r.title} className="card-cream">
              <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>{r.title}</h3>
              <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>{r.body}</p>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 40 }}>
          <Link to="/resources" className="link-inline">Browse all resources</Link>
        </div>
      </Section>

      {/* FAQ */}
      <Section tone="cream">
        <FAQ items={faqs} />
      </Section>

      <CTABand />
    </>
  )
}
